import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useAdminServiceProviders } from '../../hooks/useAdminServiceProviders';
import { type AdminProviderRiskFlag } from '../../services/adminCommandCenterService';
import {
  getAdminProviderRiskLabel,
  type AdminServiceProviderDirectoryItem,
  type AdminServiceProvidersMetric,
} from '../../services/adminServiceProvidersService';

type ProviderTypeFilter = 'all' | 'real' | 'test';
type ProviderStatusFilter =
  | 'all'
  | 'approved'
  | 'pending_approval'
  | 'changes_requested'
  | 'rejected'
  | 'profile_incomplete'
  | 'unknown';
type ProviderVisibilityFilter = 'all' | 'visible' | 'operational' | 'non_operational';
type MissingDataFilter = 'all' | AdminProviderRiskFlag;
type SortOption = 'updated_desc' | 'name_asc' | 'city_asc' | 'status_asc' | 'completeness_desc';

const statusOrder: Record<string, number> = {
  pending_approval: 1,
  changes_requested: 2,
  rejected: 3,
  profile_incomplete: 4,
  approved: 5,
  unknown: 6,
};

const formatMetricValue = (metric: AdminServiceProvidersMetric): string =>
  metric.value === null ? '—' : String(metric.value);

const getProviderTypeLabel = (provider: AdminServiceProviderDirectoryItem): string =>
  provider.isTestData ? 'Testowy' : 'Realny';

const getVisibilityLabel = (provider: AdminServiceProviderDirectoryItem): string => {
  if (provider.isOperational) return 'Operacyjny';
  if (provider.isVisible) return 'Widoczny publicznie';
  return 'Nieoperacyjny';
};

const MetricTile: React.FC<{ metric: AdminServiceProvidersMetric; tone?: 'good' | 'warning' }> = ({
  metric,
  tone,
}) => {
  const toneClass =
    tone === 'good'
      ? 'border-ratuj-300/30 bg-ratuj-300/10'
      : tone === 'warning'
        ? 'border-amber-300/30 bg-amber-300/10'
        : 'border-primary-dark-300/18 bg-primary-dark-100/10';
  return (
    <article className={`rounded-lg border p-3.5 ${toneClass}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-primary-light-300">{metric.label}</p>
        {metric.partial && (
          <span className="rounded-md border border-amber-300/25 px-1.5 py-0.5 text-[10px] font-medium text-amber-100">
            partial
          </span>
        )}
      </div>
      <p className="mt-2 text-2xl font-semibold text-primary-light-100">
        {formatMetricValue(metric)}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-primary-light-500">{metric.description}</p>
    </article>
  );
};

const Badge: React.FC<{
  children: React.ReactNode;
  tone?: 'default' | 'good' | 'warning' | 'neutral';
}> = ({ children, tone = 'default' }) => {
  const toneClass =
    tone === 'good'
      ? 'border-ratuj-300/30 bg-ratuj-300/10 text-ratuj-100'
      : tone === 'warning'
        ? 'border-amber-300/30 bg-amber-300/10 text-amber-100'
        : tone === 'neutral'
          ? 'border-primary-dark-300 bg-primary-dark-100 text-primary-light-400'
          : 'border-primary-dark-300/70 bg-primary-dark-200 text-primary-light-300';
  return (
    <span
      className={`inline-flex max-w-full items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${toneClass}`}
    >
      {children}
    </span>
  );
};

const ProviderDetail: React.FC<{ provider: AdminServiceProviderDirectoryItem }> = ({
  provider,
}) => (
  <div className="mt-3 rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-3">
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
          Dane podstawowe
        </p>
        <p className="mt-1 text-sm text-primary-light-200">
          {provider.addressSummary || 'Brak adresu'}
        </p>
        <p className="mt-1 break-words text-xs text-primary-light-500">
          {provider.ownerEmail || 'Brak emaila właściciela'}
        </p>
        <p className="mt-1 text-xs text-primary-light-500">
          {provider.phoneNumber || 'Brak telefonu'}
        </p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
          Statusy
        </p>
        <p className="mt-1 text-sm text-primary-light-200">
          {provider.verificationStatusLabel} ·{' '}
          {provider.isVerified ? 'isVerified=true' : 'isVerified=false'}
        </p>
        <p className="mt-1 text-xs text-primary-light-500">
          {provider.isTestData ? 'Profil testowy' : 'Profil realny'} ·{' '}
          {getVisibilityLabel(provider)}
        </p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
          Kompletność
        </p>
        <p className="mt-1 text-sm text-primary-light-200">
          {provider.completenessLabel} pól profilu
        </p>
        <p className="mt-1 text-xs text-primary-light-500">
          {provider.riskLabels.length ? provider.riskLabels.join(', ') : 'Bez braków danych'}
        </p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
          Daty
        </p>
        <p className="mt-1 text-sm text-primary-light-200">Utworzono: {provider.createdAtLabel}</p>
        <p className="mt-1 text-xs text-primary-light-500">
          Aktualizacja: {provider.updatedAtLabel}
        </p>
      </div>
    </div>
    <div className="mt-3 grid grid-cols-1 gap-3 border-t border-primary-dark-300/55 pt-3 md:grid-cols-2 xl:grid-cols-4">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
          Specjalizacje
        </p>
        <p className="mt-1 text-sm text-primary-light-300">
          {provider.specializations.length
            ? provider.specializations.join(', ')
            : 'Brak specjalizacji'}
        </p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
          Metody realizacji
        </p>
        <p className="mt-1 text-sm text-primary-light-300">
          {provider.fulfillmentModeLabels.length
            ? provider.fulfillmentModeLabels.join(', ')
            : 'Brak aktywnych metod'}
        </p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
          Godziny i lokalizacja
        </p>
        <p className="mt-1 text-sm text-primary-light-300">{provider.businessHoursSummary}</p>
        <p className="mt-1 text-xs text-primary-light-500">{provider.locationSummary}</p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
          Zdjęcie
        </p>
        <p className="mt-1 text-sm text-primary-light-300">{provider.imageStatus}</p>
      </div>
    </div>
  </div>
);

const ProviderRow: React.FC<{
  provider: AdminServiceProviderDirectoryItem;
  expanded: boolean;
  onToggle: () => void;
}> = ({ provider, expanded, onToggle }) => (
  <article className="rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-3.5">
    <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="max-w-full break-words text-base font-semibold text-primary-light-100">
            {provider.serviceName}
          </h2>
          <Badge tone={provider.isTestData ? 'neutral' : 'default'}>
            {getProviderTypeLabel(provider)}
          </Badge>
          <Badge
            tone={provider.isOperational ? 'good' : provider.isVisible ? 'default' : 'neutral'}
          >
            {getVisibilityLabel(provider)}
          </Badge>
          <Badge tone={provider.verificationStatus === 'pending_approval' ? 'warning' : 'default'}>
            {provider.verificationStatusLabel}
          </Badge>
        </div>
        <p className="mt-1 text-sm text-primary-light-400">
          {provider.addressSummary || provider.city || 'Brak miasta i adresu'}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-primary-light-500">
          <span>Kompletność {provider.completenessLabel}</span>
          <span>Aktualizacja {provider.updatedAtLabel}</span>
          <span>{provider.ownerEmail || 'Brak emaila'}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {provider.riskLabels.length ? (
            provider.riskLabels.slice(0, 3).map((label) => (
              <Badge key={label} tone="warning">
                {label}
              </Badge>
            ))
          ) : (
            <Badge tone="good">Bez braków profilu</Badge>
          )}
          {provider.riskLabels.length > 3 && (
            <Badge tone="neutral">+{provider.riskLabels.length - 3}</Badge>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row xl:flex-shrink-0 xl:justify-end">
        {provider.publicProfilePath && (
          <Link
            to={provider.publicProfilePath}
            className="inline-flex min-h-[2.25rem] items-center justify-center rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
          >
            Zobacz profil
          </Link>
        )}
        {provider.verificationPath && (
          <Link
            to={provider.verificationPath}
            className="inline-flex min-h-[2.25rem] items-center justify-center rounded-lg border border-ratuj-300/35 px-3 py-2 text-sm font-medium text-ratuj-100 hover:border-ratuj-200/60"
          >
            Weryfikacja
          </Link>
        )}
        {provider.toolsPath && (
          <Link
            to={provider.toolsPath}
            className="inline-flex min-h-[2.25rem] items-center justify-center rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
          >
            Narzędzia
          </Link>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex min-h-[2.25rem] items-center justify-center gap-1.5 rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200 hover:border-primary-dark-200 hover:text-primary-light-100"
          aria-expanded={expanded}
        >
          Szczegóły
          {expanded ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
    {expanded && <ProviderDetail provider={provider} />}
  </article>
);

const AdminServiceProvidersPage: React.FC = () => {
  const { data, providers, loading, loadingMore, error, refresh, loadMore } =
    useAdminServiceProviders();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ProviderTypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<ProviderStatusFilter>('all');
  const [visibilityFilter, setVisibilityFilter] = useState<ProviderVisibilityFilter>('all');
  const [missingDataFilter, setMissingDataFilter] = useState<MissingDataFilter>('all');
  const [sortOption, setSortOption] = useState<SortOption>('updated_desc');
  const [expandedUid, setExpandedUid] = useState<string | null>(null);

  const filteredProviders = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const filtered = providers.filter((provider) => {
      if (normalizedSearch && !provider.searchText.includes(normalizedSearch)) return false;
      if (typeFilter === 'real' && !provider.isReal) return false;
      if (typeFilter === 'test' && !provider.isTestData) return false;
      if (statusFilter !== 'all' && provider.verificationStatus !== statusFilter) return false;
      if (visibilityFilter === 'visible' && !provider.isVisible) return false;
      if (visibilityFilter === 'operational' && !provider.isOperational) return false;
      if (visibilityFilter === 'non_operational' && provider.isOperational) return false;
      if (missingDataFilter !== 'all' && !provider.riskFlags.includes(missingDataFilter)) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortOption === 'name_asc') return a.serviceName.localeCompare(b.serviceName, 'pl');
      if (sortOption === 'city_asc') return (a.city || '').localeCompare(b.city || '', 'pl');
      if (sortOption === 'status_asc') {
        return (
          (statusOrder[a.verificationStatus] || 99) - (statusOrder[b.verificationStatus] || 99)
        );
      }
      if (sortOption === 'completeness_desc') {
        return (
          b.completenessScore - a.completenessScore ||
          a.serviceName.localeCompare(b.serviceName, 'pl')
        );
      }
      return (b.updatedAt?.getTime() || 0) - (a.updatedAt?.getTime() || 0);
    });
  }, [
    missingDataFilter,
    providers,
    search,
    sortOption,
    statusFilter,
    typeFilter,
    visibilityFilter,
  ]);

  if (loading && !data) {
    return (
      <section className="space-y-4" role="status" aria-label="Ładowanie katalogu serwisów">
        <div className="rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-5">
          <div className="h-6 w-40 animate-pulse rounded bg-primary-dark-300" />
          <div className="mt-3 h-4 w-full max-w-xl animate-pulse rounded bg-primary-dark-300/75" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
          {[0, 1, 2, 3, 4, 5].map((item) => (
            <div
              key={item}
              className="h-28 animate-pulse rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75"
            />
          ))}
        </div>
      </section>
    );
  }

  if (error && !data) {
    return (
      <section className="rounded-lg border border-status-error-500/40 bg-status-error-500/10 p-5 text-status-error-200">
        <h1 className="text-base font-semibold">Katalog serwisów niedostępny</h1>
        <p className="mt-2 text-sm text-status-error-300">{error}</p>
        <button
          type="button"
          onClick={() => void refresh()}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-ratuj-400 px-3 py-2 text-sm font-medium text-white hover:bg-ratuj-300"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Ponów odczyt
        </button>
      </section>
    );
  }

  if (!data) return null;

  const metrics = data.metrics;

  return (
    <section className="space-y-4 sm:space-y-5">
      <header className="border-b border-primary-dark-300/18 px-1 pb-4 sm:px-0 sm:pb-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-primary-light-100 sm:text-2xl">Serwisy</h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-primary-light-300">
              Przegląd profili serwisów, statusów i braków danych.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-primary-light-400">
            <span className="rounded-lg border border-primary-dark-300 px-2.5 py-1.5">
              Odczyt: {data.readAt.toLocaleString('pl-PL')}
            </span>
            {data.isPartial && (
              <span className="rounded-lg border border-amber-300/30 bg-amber-300/10 px-2.5 py-1.5 text-amber-100">
                Dane częściowe
              </span>
            )}
            <button
              type="button"
              onClick={() => void refresh()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary-dark-300 px-2.5 py-1.5 text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Odśwież
            </button>
          </div>
        </div>
      </header>

      {data.partialError && (
        <div className="rounded-lg border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-amber-100">
          {data.partialError}
        </div>
      )}
      {error && data && (
        <div className="rounded-lg border border-status-error-500/40 bg-status-error-500/10 p-3 text-sm text-status-error-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
        <MetricTile metric={metrics.total} />
        <MetricTile metric={metrics.real} />
        <MetricTile metric={metrics.test} />
        <MetricTile metric={metrics.operational} tone="good" />
        <MetricTile metric={metrics.pending} tone="warning" />
        <MetricTile
          metric={metrics.withMissingData}
          tone={(metrics.withMissingData.value ?? 0) > 0 ? 'warning' : undefined}
        />
      </div>

      <section className="border-t border-primary-dark-300/16 px-1 py-4 sm:px-0 sm:py-5">
        <div className="mb-4 flex flex-col gap-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ratuj-200">
            Filtry
          </p>
          <h2 className="text-base font-semibold text-primary-light-100">Katalog operacyjny</h2>
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-6">
          <label className="lg:col-span-2 text-xs text-primary-light-400">
            Szukaj
            <div className="relative mt-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-primary-light-500" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nazwa, miasto, email"
                className="w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 py-2 pl-9 text-sm text-primary-light-100 placeholder:text-primary-light-500"
              />
            </div>
          </label>
          <label className="text-xs text-primary-light-400">
            Typ
            <select
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value as ProviderTypeFilter)}
              className="mt-1 w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 text-sm text-primary-light-100"
            >
              <option value="all">Wszystkie</option>
              <option value="real">Realne</option>
              <option value="test">Testowe</option>
            </select>
          </label>
          <label className="text-xs text-primary-light-400">
            Status
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as ProviderStatusFilter)}
              className="mt-1 w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 text-sm text-primary-light-100"
            >
              <option value="all">Wszystkie</option>
              <option value="approved">Approved</option>
              <option value="pending_approval">Pending</option>
              <option value="changes_requested">Do poprawek</option>
              <option value="rejected">Odrzucone</option>
              <option value="profile_incomplete">Niekompletne</option>
              <option value="unknown">Nieznane</option>
            </select>
          </label>
          <label className="text-xs text-primary-light-400">
            Widoczność
            <select
              value={visibilityFilter}
              onChange={(event) =>
                setVisibilityFilter(event.target.value as ProviderVisibilityFilter)
              }
              className="mt-1 w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 text-sm text-primary-light-100"
            >
              <option value="all">Wszystkie</option>
              <option value="visible">Widoczne publicznie</option>
              <option value="operational">Operacyjne</option>
              <option value="non_operational">Nieoperacyjne</option>
            </select>
          </label>
          <label className="text-xs text-primary-light-400">
            Sortowanie
            <select
              value={sortOption}
              onChange={(event) => setSortOption(event.target.value as SortOption)}
              className="mt-1 w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 text-sm text-primary-light-100"
            >
              <option value="updated_desc">Ostatnio aktualizowane</option>
              <option value="name_asc">Nazwa A-Z</option>
              <option value="city_asc">Miasto A-Z</option>
              <option value="status_asc">Status</option>
              <option value="completeness_desc">Kompletność profilu</option>
            </select>
          </label>
          <label className="lg:col-span-2 text-xs text-primary-light-400">
            Braki profilu
            <div className="relative mt-1">
              <FunnelIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-primary-light-500" />
              <select
                value={missingDataFilter}
                onChange={(event) => setMissingDataFilter(event.target.value as MissingDataFilter)}
                className="w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 py-2 pl-9 text-sm text-primary-light-100"
              >
                <option value="all">Wszystkie</option>
                <option value="missingLocation">
                  {getAdminProviderRiskLabel('missingLocation')}
                </option>
                <option value="missingProfileImage">
                  {getAdminProviderRiskLabel('missingProfileImage')}
                </option>
                <option value="missingBusinessHours">
                  {getAdminProviderRiskLabel('missingBusinessHours')}
                </option>
                <option value="missingServiceFulfillment">
                  {getAdminProviderRiskLabel('missingServiceFulfillment')}
                </option>
                <option value="missingSpecializations">
                  {getAdminProviderRiskLabel('missingSpecializations')}
                </option>
              </select>
            </div>
          </label>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-primary-light-100">
              Wyniki ({filteredProviders.length})
            </h2>
            <p className="mt-1 text-sm text-primary-light-500">
              Odczyt listy jest stronicowany po `updatedAt`; filtry i sortowanie działają lokalnie
              na wczytanym zbiorze.
            </p>
          </div>
          {data.hasMore && (
            <span className="text-xs text-primary-light-500">
              Wczytano {providers.length}; dostępne kolejne rekordy.
            </span>
          )}
        </div>

        {providers.length === 0 ? (
          <div className="rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-5 text-sm text-primary-light-400">
            Brak dokumentów `serviceProviders` w odczytanym katalogu.
          </div>
        ) : filteredProviders.length === 0 ? (
          <div className="rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-5 text-sm text-primary-light-400">
            Brak wyników dla aktualnych filtrów. Zmień filtr albo wczytaj kolejną stronę katalogu.
          </div>
        ) : (
          <div className="space-y-2.5">
            {filteredProviders.map((provider) => (
              <ProviderRow
                key={provider.uid}
                provider={provider}
                expanded={expandedUid === provider.uid}
                onToggle={() =>
                  setExpandedUid((current) => (current === provider.uid ? null : provider.uid))
                }
              />
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-start gap-2 text-xs leading-relaxed text-primary-light-500">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-200" />
            Widok jest read-only: nie wykonuje approve/reject, nie zapisuje notatek i nie tworzy
            agregatów.
          </p>
          {data.hasMore && (
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="inline-flex min-h-[2.5rem] items-center justify-center rounded-lg border border-primary-dark-300 px-4 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loadingMore ? 'Wczytywanie...' : 'Wczytaj więcej'}
            </button>
          )}
        </div>
      </section>
    </section>
  );
};

export default AdminServiceProvidersPage;
